import {
    ActionField,
    ActionImpactData,
    ActionPlanAction,
    TargetSsqProperty,
} from "src/api/client-api/ActionPlan/ActionPlanAction";

type Options = {
    actionPlanAction: ActionPlanAction | undefined;
    targetProperties: TargetSsqProperty[];
    actionFields: ActionField[];
    scoreId: number;
};
export const formatSimpleComparisonTable = (options: Options): SingleImpactTableList | null => {
    const { actionPlanAction, targetProperties, actionFields, scoreId } = options;

    if (actionPlanAction === undefined) {
        return null;
    }

    let totalRowCount = 0;
    const propertiesId = targetProperties.map((v) => v.id);
    const simpleTable = Object.entries(actionPlanAction.impactData).flatMap(
        ([propertyIdString, impactDataPerPosition]) => {
            if (impactDataPerPosition === undefined) return [];
            if (propertiesId.includes(Number(propertyIdString)) === false) return [];

            const actionsImpact = new Set<number>();
            const actionFieldImpactByAction = Object.entries(impactDataPerPosition).flatMap(
                ([position, actionImpactData]) => {
                    if (actionImpactData === undefined) return [];
                    const property = targetProperties.find((property) => property.id.toString() === propertyIdString);

                    if (property === undefined) return [];

                    const propertyActionField = actionFields.find((field) => {
                        return field.actionFormulas?.find((formula) => formula.targetSsqProperty?.id === property.id);
                    });

                    const isMultivalued = propertyActionField?.isMultivalue;
                    if (isMultivalued) return [];

                    const totalImpact = actionImpactData.reduce((acc, impact) => {
                        const impactScore = impact.impactScores[scoreId];

                        actionsImpact.add(impact.actionId);
                        if (impactScore !== undefined) {
                            return acc + impactScore;
                        }

                        return acc;
                    }, 0);

                    const formattedSummedActionsImpact = totalImpact;

                    const propertyParentId = property.parent?.id;
                    const propertiesTree = actionPlanAction.action.propertiesTree;
                    const propertyTreeSubproperty = propertiesTree?.[propertyParentId ?? 0];
                    const firstSubpropertyToShowId = propertyTreeSubproperty?.[0];

                    const treeElements = actionPlanAction.propertiesTreeElements;
                    const subpropertyElement = treeElements.find((subproperty) => {
                        const isPosition = subproperty.position === Number(position);
                        const ssqPropertyId = subproperty.ssq_property_id === firstSubpropertyToShowId;
                        return isPosition && ssqPropertyId;
                    });

                    totalRowCount += actionImpactData.length;
                    const shouldDisplaySum = actionImpactData.length > 1;
                    const isSingleActionImpact = actionsImpact.size === 1;
                    const hasMultipleActions = actionsImpact.size > 1;

                    const subpropertyValue = subpropertyElement?.value;

                    const impactByAction: SingleImpactByAction = {
                        position,
                        isSingleActionImpact,
                        hasMultipleActions,
                        property,
                        rows: actionImpactData,
                        formattedSummedActionsImpact,
                        subpropertyValue,
                        shouldDisplaySum,
                    };

                    return [impactByAction];
                },
            );

            return [...actionFieldImpactByAction];
        },
    );

    const impactTableList: SingleImpactTableList = {
        totalRowCount,
        tableData: simpleTable,
    };
    return impactTableList;
};

export type SingleImpactByAction = {
    position: string;
    property: TargetSsqProperty;
    rows: ActionImpactData[];
    subpropertyValue: string | undefined;
    formattedSummedActionsImpact: number | null;
    shouldDisplaySum: boolean;
    isSingleActionImpact: boolean;
    hasMultipleActions: boolean;
};

export type SingleImpactTableList = {
    tableData: SingleImpactByAction[];
    totalRowCount: number;
};
